/* eslint-disable no-magic-numbers -- 3D navigation math and rendering tolerances */
import {
  type Mesh,
  Quaternion,
  Tool,
  Vector2,
  Vector3,
  Zoom,
} from '@technology/web3d';
import type { MeshLine } from '@technology/web3d-plugin-threejs';
import type { Api } from '@technology/web3d/dist/esnext/Api.js';
import type { PointerInput } from '@technology/web3d/dist/esnext/InputHandler.js';
import type { Observable, Subscription } from 'rxjs';
import { applyOrbitDelta, computePanOffset } from './path-navigation-math';
import {
  directionToSpherical,
  MouseButtonValue,
  Vector3Const,
} from './web3d-internals';

export abstract class PathNavigationViewControl extends Tool {
  readonly #api: Api;
  /** direction of the alignment at a given time */
  #currentAlignmentDirection: Quaternion = new Quaternion();

  protected get _currentAlignmentDirection(): Quaternion {
    return this.#currentAlignmentDirection;
  }

  protected set _currentAlignmentDirection(value: Quaternion) {
    this.#currentAlignmentDirection = value;
  }
  /** a point on an alignment that the tool/camera is using as a reference point */
  #currentAlignmentPoint = new Vector3();

  protected get _currentAlignmentPoint(): Vector3 {
    return this.#currentAlignmentPoint;
  }

  protected set _currentAlignmentPoint(value: Vector3) {
    this.#currentAlignmentPoint = value;
  }
  #currentLineSegmentId = 0;

  protected get _currentLineSegmentId(): number {
    return this.#currentLineSegmentId;
  }

  protected set _currentLineSegmentId(value: number) {
    this.#currentLineSegmentId = value;
  }
  #currentLineSegmentFraction = 0;

  protected get _currentLineSegmentFraction(): number {
    return this.#currentLineSegmentFraction;
  }

  protected set _currentLineSegmentFraction(value: number) {
    this.#currentLineSegmentFraction = value;
  }

  #centerMarker!: Mesh;

  protected get _centerMarker(): Mesh {
    return this.#centerMarker;
  }

  protected set _centerMarker(value: Mesh) {
    this.#centerMarker = value;
  }
  #alignmentMarker!: MeshLine;

  protected get _alignmentMarker(): MeshLine {
    return this.#alignmentMarker;
  }

  protected set _alignmentMarker(value: MeshLine) {
    this.#alignmentMarker = value;
  }

  #crossSectionEnabled = false;

  protected get _crossSectionEnabled(): boolean {
    return this.#crossSectionEnabled;
  }

  protected set _crossSectionEnabled(value: boolean) {
    this.#crossSectionEnabled = value;
  }
  // Pan
  #panSpeed = 0;
  #panDragHandle: Subscription | undefined = undefined;

  protected get _panDragHandle(): Subscription | undefined {
    return this.#panDragHandle;
  }

  protected set _panDragHandle(value: Subscription | undefined) {
    this.#panDragHandle = value;
  }
  #verticalPanDragHandle: Subscription | undefined = undefined;

  protected get _verticalPanDragHandle(): Subscription | undefined {
    return this.#verticalPanDragHandle;
  }

  protected set _verticalPanDragHandle(value: Subscription | undefined) {
    this.#verticalPanDragHandle = value;
  }
  #panDragObservable: Observable<PointerInput>;

  protected get _panDragObservable(): Observable<PointerInput> {
    return this.#panDragObservable;
  }

  protected set _panDragObservable(value: Observable<PointerInput>) {
    this.#panDragObservable = value;
  }
  #verticalPanDragObservable: Observable<PointerInput>;

  protected get _verticalPanDragObservable(): Observable<PointerInput> {
    return this.#verticalPanDragObservable;
  }

  protected set _verticalPanDragObservable(value: Observable<PointerInput>) {
    this.#verticalPanDragObservable = value;
  }
  #previousEvent!: PointerInput;

  // Orbit
  readonly #EPS = 0.02;

  readonly #rotationSpeed = new Vector2(0.003, 0.007);
  #orbitDragHandle: Subscription | undefined = undefined;

  protected get _orbitDragHandle(): Subscription | undefined {
    return this.#orbitDragHandle;
  }

  protected set _orbitDragHandle(value: Subscription | undefined) {
    this.#orbitDragHandle = value;
  }
  #orbitDragObservable: Observable<PointerInput>;

  protected get _orbitDragObservable(): Observable<PointerInput> {
    return this.#orbitDragObservable;
  }

  protected set _orbitDragObservable(value: Observable<PointerInput>) {
    this.#orbitDragObservable = value;
  }
  readonly #offset = new Vector3();

  #originalPosition!: Vector3;
  #originalDirection!: Vector3;
  #originalUp!: Vector3;
  #rotationPoint: Vector3 | undefined;
  #calculatingRotationPoint = false;

  #startEvent!: PointerInput;

  #verticalPanOnly = true;
  #viewDirectionBeforeOrbit!: Quaternion;

  constructor(api: Api) {
    super();
    this.#api = api;
    // Pan
    this.#panDragObservable = api.inputHandler.createDragObservable(
      { button: MouseButtonValue.right },
      (e) => this.#panDownCallback(e),
      (e) => this.#panMoveCallback(e),
      (e) => this._panUpCallback(e)
    );

    // Vertical pan
    this.#verticalPanDragObservable = api.inputHandler.createDragObservable(
      { button: MouseButtonValue.middle },
      (e) => this.#panDownCallback(e),
      (e) => this.#panMoveCallback(e),
      (e) => this._panUpCallback(e)
    );

    // Orbit
    this.#orbitDragObservable = api.inputHandler.createDragObservable(
      { button: MouseButtonValue.left },
      (e) => this._orbitDownCallback(e),
      (e) => this._orbitMoveCallback(e),
      (e) => this._orbitUpCallback(e)
    );
  }

  public get enabled(): boolean {
    return !!this.#verticalPanDragHandle && !!this.#orbitDragHandle;
  }

  /** Is camera looking back on the alignment direction */
  protected get _lookingBackwards(): boolean {
    const direction = (q: Quaternion): Vector3 => {
      return Vector3Const.down.clone().applyQuaternion(q);
    };
    return (
      direction(this.#api.camera.quaternion).dot(
        direction(this.#currentAlignmentDirection)
      ) < 0
    );
  }

  // Pan
  public set verticalPanOnly(value: boolean) {
    this.#verticalPanOnly = value;
  }

  public get verticalPanOnly(): boolean {
    return this.#verticalPanOnly;
  }

  // todo investigate this: which actual values are getting set ?
  set #speed(point: Vector3 | null) {
    let distance: number;
    if (point) distance = this.#api.camera.position.distanceTo(point);
    else {
      // If no point was picked assume camera is far away from any model
      distance = Zoom.getDistanceToNearestModel(
        this.#api.camera.position,
        this.#api.models,
        10,
        1000
      );
    }
    const viewWorldSize = this.#api.camera.getViewWorldSize(distance) * 2;
    this.#panSpeed =
      viewWorldSize /
      Math.max(
        this.#api.container.clientWidth,
        this.#api.container.clientHeight
      );
  }

  async #panDownCallback(event: PointerInput): Promise<PointerInput> {
    const point = await this.#api.picker.pickForNavigation(
      new Vector2(event.x, event.y)
    );
    this.#speed = point ?? null;
    this.#previousEvent = event;
    return event;
  }

  readonly #panMoveCallback = (event: PointerInput): PointerInput => {
    const offset = computePanOffset({
      deltaX: event.screenX - this.#previousEvent.screenX,
      deltaY: event.screenY - this.#previousEvent.screenY,
      panSpeed: this.#panSpeed,
      verticalOnly: this.verticalPanOnly,
      crossSection: this.#crossSectionEnabled,
      cameraQuaternion: this.#api.camera.quaternion,
      alignmentQuaternion: this.#currentAlignmentDirection,
      lookingBackwards: this._lookingBackwards,
    });

    this.#api.camera.position.add(offset);
    this.#centerMarker.position.add(offset);
    this.#api.camera.callListeners();

    this.#previousEvent = event;
    return event;
  };

  protected _panUpCallback(event: PointerInput): PointerInput {
    this.#api.renderingManager.redraw();
    return event;
  }

  // Orbit
  protected _orbitDownCallback(event: PointerInput): PointerInput {
    if (!this.#crossSectionEnabled) {
      this.#viewDirectionBeforeOrbit = this.#api.camera.quaternion.clone();
      this.#startEvent = event;
      this.#rotationPoint = undefined;
    }
    return event;
  }

  protected async _orbitMoveCallback(event: PointerInput): Promise<void> {
    if (!this.#crossSectionEnabled) {
      if (this.#calculatingRotationPoint) return;
      if (!this.#rotationPoint) {
        this.#calculatingRotationPoint = true;
        this.#rotationPoint = this.#centerMarker.position;
        this.#updateOriginalCamera();
        this.#calculatingRotationPoint = false;
      }

      const delta = new Vector2(
        event.screenX - this.#startEvent.screenX,
        event.screenY - this.#startEvent.screenY
      );
      const sphericalDirection = directionToSpherical(
        this.#originalDirection,
        this.#originalUp
      );
      this._translateCamera(delta, sphericalDirection);
      this.#api.camera.callListeners();
    }
  }

  protected _orbitUpCallback(event: PointerInput): PointerInput {
    if (!this.#crossSectionEnabled) {
      this.#viewDirectionBeforeOrbit.multiplyQuaternions(
        this.#api.camera.quaternion,
        this.#viewDirectionBeforeOrbit.invert()
      );

      // Reset view direction and rotation point
      this.#viewDirectionBeforeOrbit = new Quaternion();
      this.#rotationPoint = undefined;
    }
    return event;
  }

  protected _translateCamera(
    delta: Vector2,
    sphericalDirection: Vector2
  ): void {
    applyOrbitDelta(sphericalDirection, delta, this.#rotationSpeed, this.#EPS);
    if (!this.#rotationPoint) return;
    this.#api.camera.rotateSpherical(
      this.#rotationPoint,
      this.#offset,
      sphericalDirection
    );
  }

  readonly #updateOriginalCamera = ((): (() => void) => {
    const right = new Vector3();

    return (): void => {
      this.#originalUp ??= new Vector3();
      this.#originalDirection = this.#api.camera.getWorldDirection(
        new Vector3()
      );
      this.#originalPosition = this.#api.camera.position.clone();

      this.#originalUp.copy(Vector3Const.forward);
      right.copy(Vector3Const.right);

      this.#originalUp.applyQuaternion(this.#api.camera.quaternion);
      right.applyQuaternion(this.#api.camera.quaternion);

      const offset = new Vector3()
        .copy(this.#rotationPoint ?? this.#centerMarker.position)
        .sub(this.#originalPosition);
      const direction = this.#originalDirection;

      this.#offset.set(
        offset.dot(right),
        offset.dot(direction),
        offset.dot(this.#originalUp)
      );
    };
  })();
}
